import { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '../design-system';
import { AVATAR_MAX_ZOOM, AVATAR_VIEW_PX, clampCrop, coverScale, renderAvatar, type CropState } from '../../lib/avatarCrop';

interface AvatarCropModalProps {
  /** Data URL (or object URL) of the picked photo. */
  src: string;
  /** Called with a square JPEG of what's inside the circle. */
  onSave: (photo: Blob) => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Preview a picked photo inside the circle it will appear in, drag to recenter
 * and zoom, then save. The result is always a standard JPEG, so a photo the
 * browser can't decode (e.g. HEIC) is caught here instead of saved as a link
 * that never loads.
 */
export function AvatarCropModal({ src, onSave, onCancel }: AvatarCropModalProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [crop, setCrop] = useState<CropState>({ zoom: 1, x: 0, y: 0 });
  const [unreadable, setUnreadable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onCancel]);

  const update = (next: CropState) => {
    if (natural) setCrop(clampCrop(natural.w, natural.h, next));
  };

  const scale = natural ? coverScale(natural.w, natural.h) * crop.zoom : 1;

  async function handleSave() {
    const img = imgRef.current;
    if (!img || !natural || saving) return;
    setSaving(true);
    setError(null);
    try {
      const blob = await renderAvatar(img, crop);
      if (!blob) throw new Error('encode');
      await onSave(blob);
    } catch {
      setError('Could not save your photo. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={() => !saving && onCancel()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Adjust your photo"
        className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-app-fg self-start">Adjust your photo</h3>

        {unreadable ? (
          <p className="text-sm text-app-muted text-center py-6">
            This photo's format can't be used here. Please pick a JPG or PNG image.
          </p>
        ) : (
          <>
            <div
              className="relative overflow-hidden rounded-full bg-black select-none"
              style={{ width: AVATAR_VIEW_PX, height: AVATAR_VIEW_PX, touchAction: 'none', cursor: 'grab' }}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                drag.current = { px: e.clientX, py: e.clientY, x: crop.x, y: crop.y };
              }}
              onPointerMove={(e) => {
                const d = drag.current;
                if (!d) return;
                update({ ...crop, x: d.x + (e.clientX - d.px), y: d.y + (e.clientY - d.py) });
              }}
              onPointerUp={() => { drag.current = null; }}
              onPointerCancel={() => { drag.current = null; }}
            >
              <img
                ref={imgRef}
                src={src}
                alt="Preview of your photo"
                draggable={false}
                onLoad={(e) => {
                  const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                  if (!w || !h) { setUnreadable(true); return; }
                  setNatural({ w, h });
                }}
                onError={() => setUnreadable(true)}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: natural ? natural.w * scale : undefined,
                  height: natural ? natural.h * scale : undefined,
                  maxWidth: 'none',
                  transform: `translate(calc(-50% + ${crop.x}px), calc(-50% + ${crop.y}px))`,
                  visibility: natural ? 'visible' : 'hidden',
                  pointerEvents: 'none',
                }}
              />
              {!natural && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spinner size="sm" className="text-white" />
                </div>
              )}
            </div>

            <p className="text-xs text-app-muted">Drag to recenter. Use the slider to zoom.</p>

            <label className="w-full flex items-center gap-3 text-xs text-app-muted">
              <span>Zoom</span>
              <input
                type="range"
                min={1}
                max={AVATAR_MAX_ZOOM}
                step={0.01}
                value={crop.zoom}
                disabled={!natural || saving}
                onChange={(e) => update({ ...crop, zoom: Number(e.target.value) })}
                className="flex-1"
                aria-label="Zoom"
              />
            </label>
          </>
        )}

        {error && <p className="text-xs text-crimson self-start">{error}</p>}

        <div className="flex gap-2 w-full justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
          {!unreadable && (
            <Button size="sm" onClick={() => void handleSave()} disabled={!natural || saving} loading={saving}>
              Save photo
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
